import { useState, useRef, useEffect } from 'react';
import { Modal, Tab } from 'common/components';
import { useFormContext } from 'react-hook-form';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';
import { DefinePartyStatusTab } from './DefinePartyStatusTab';
import { PartyStatusTransitionsTab } from './PartyStatusTransitionsTab';

enum PartyStatusTab {
    DEFINE = 'define',
    TRANSITIONS = 'transitions',
}

const partyStatusTabs = [
    { id: PartyStatusTab.DEFINE, label: 'Define Party Status' },
    { id: PartyStatusTab.TRANSITIONS, label: 'Party Status Transitions' },
];

/**
 * The workflow's status model. Statuses are only editable while the workflow is
 * being created, because parties store their stage as a plain string: renaming
 * or removing a status afterwards would strand everyone already on it. A saved
 * workflow opens with transitionsOnly, since rules only apply going forward.
 */
export const PartyStatusModal = ({
    isOpen,
    onClose,
    transitionsOnly = false,
}: {
    isOpen: boolean;
    onClose: () => void;
    transitionsOnly?: boolean;
}) => {
    const { getValues, setValue } = useFormContext<WorkflowConfigFormData>();
    const [activeTab, setActiveTab] = useState<PartyStatusTab>(PartyStatusTab.DEFINE);

    // Cancel restores what was there on open, so a half-finished edit does not
    // leak into the workflow the user then saves.
    const snapshotRef = useRef<{
        partyStatuses: WorkflowConfigFormData['partyStatuses'];
        statusTransitions: WorkflowConfigFormData['statusTransitions'];
    } | null>(null);

    useEffect(() => {
        if (isOpen) {
            snapshotRef.current = {
                partyStatuses: structuredClone(getValues('partyStatuses') ?? []),
                statusTransitions: structuredClone(getValues('statusTransitions') ?? []),
            };
        }
    }, [isOpen, getValues]);

    const handleClose = () => {
        if (snapshotRef.current) {
            setValue('partyStatuses', snapshotRef.current.partyStatuses);
            setValue('statusTransitions', snapshotRef.current.statusTransitions);
        }
        onClose();
    };

    return (
        <Modal
            title={transitionsOnly ? 'Status Transitions' : 'Party Status'}
            isOpen={isOpen}
            onClose={handleClose}
            onDone={onClose}
            closeOnOverlayClick={false}
        >
            <div style={{ minHeight: '440px' }}>
                {transitionsOnly ? (
                    <PartyStatusTransitionsTab />
                ) : (
                    <>
                        <Tab
                            variant='buttonStyle'
                            tabs={partyStatusTabs}
                            onTabChange={(id: string) => setActiveTab(id as PartyStatusTab)}
                        />
                        <div className='mt-lg'>
                            {activeTab === PartyStatusTab.DEFINE ? (
                                <DefinePartyStatusTab />
                            ) : (
                                <PartyStatusTransitionsTab />
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
